import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import PartnerEntry from '../components/PartnerEntry';

export default function Pricing() {
  return (
    <>
      <PageHero
        eyebrow="Pricing"
        title="Free for owners. Capital partners"
        emphasis="pay to play."
        lede="OfficerCapital makes its money from the partners on the other side of the table — not from the owners listing their companies. There are no listing fees, no success fees, and no per-message charges for owners. Ever."
      />

      {/* OWNER PRICING */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">For guard company owners</div>
              <h2 className="oc-h2">
                Free, on <em className="oc-em">every action</em>.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              We built the platform around the idea that owners shouldn't pay to take a meeting.
              Here's the entire fee schedule for owners — there is nothing else.
            </p>
          </div>

          <div className="oc-card" style={{ padding: '8px 32px' }}>
            <PriceRow what="Create a listing" price="Free" priceColor="var(--oc-green-deep)" />
            <hr className="oc-rule" />
            <PriceRow what="Edit, pause, or remove your listing" price="Free" priceColor="var(--oc-green-deep)" />
            <hr className="oc-rule" />
            <PriceRow what="View who's interested" price="Free" priceColor="var(--oc-green-deep)" />
            <hr className="oc-rule" />
            <PriceRow what="Approve and message capital partners" price="Free" priceColor="var(--oc-green-deep)" />
            <hr className="oc-rule" />
            <PriceRow what="Close a financing, investment, or sale" price="Free" priceColor="var(--oc-green-deep)" sub="No success fee. Whatever you negotiate is what you keep." />
          </div>
        </div>
      </section>

      {/* PARTNER PRICING */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">For capital partners</div>
              <h2 className="oc-h2">
                Subscription, plus <em className="oc-em">per-deal access</em>.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              Partners pay a monthly subscription to access the marketplace and a per-deal unlock fee for any specific
              listing they want to evaluate. Acquirers pay more than lenders or investors because the deals are larger
              and the diligence is heavier.
            </p>
          </div>

          <div className="oc-grid-3">
            <PartnerPlan
              label="Lenders"
              price="$500"
              cadence="/month"
              unlock="$500 per unlock"
              points={[
                'Manual application review',
                'Anonymized listing browser',
                'Full file access on unlock',
                'Direct messaging with approved owners',
              ]}
            />
            <PartnerPlan
              label="Investors"
              price="$500"
              cadence="/month"
              unlock="$500 per unlock"
              points={[
                'Manual application review',
                'Anonymized listing browser',
                'Full file access on unlock',
                'Direct messaging with approved owners',
              ]}
            />
            <PartnerPlan
              label="Acquirers"
              price="$1000"
              cadence="/month"
              unlock="$1,500 per unlock"
              points={[
                'Manual application review',
                'Anonymized listing browser',
                'Full file access on unlock',
                'Direct messaging with approved owners',
              ]}
              featured
            />
          </div>

          <div className="oc-card oc-mt-4" style={{ background: 'var(--oc-paper)' }}>
            <div className="oc-row" style={{ gap: 14, alignItems: 'flex-start' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--oc-green-deep)" strokeWidth="1.5" style={{ flexShrink: 0, marginTop: 2 }}>
                <path d="M12 2 L4 6 v6 c0 5 3.5 9 8 10 c4.5-1 8-5 8-10 V6 z" />
                <path d="M9 12 l2 2 l4-4" />
              </svg>
              <div>
                <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--oc-ink)', marginBottom: 4 }}>
                  OfficerApps customer discount
                </div>
                <div style={{ fontSize: 14, color: 'var(--oc-body)', lineHeight: 1.6 }}>
                  When the owner of a listing is an OfficerApps customer, capital partners pay <strong style={{ color: 'var(--oc-green-deep)', fontWeight: 500 }}>50% less</strong>{' '}
                  to unlock — meaning more partners reach out, and your deal moves faster. The discount is automatic.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-mb-5">
            <div className="oc-eyebrow-gray oc-mb-2">Common questions</div>
            <h2 className="oc-h2">
              Things owners ask before they <em className="oc-em">list</em>.
            </h2>
          </div>

          <div className="oc-stack-lg" style={{ maxWidth: 820 }}>
            <Faq q="Really, no success fee? How do you make money?">
              Capital partners pay a monthly subscription and a per-deal unlock fee. That's the entire revenue model.
              The owners listing their companies are why partners pay — and we'd rather have a marketplace where owners
              show up freely than one where every transaction has friction.
            </Faq>
            <Faq q="What if I list and then change my mind?">
              Pause or remove your listing in one click. Partners who already unlocked it lose access immediately.
              No fees, no waiting period, no questions.
            </Faq>
            <Faq q="Do partners get a refund if I don't approve them?">
              No. The unlock fee buys access to your listing file — it doesn't buy the right to a conversation with you.
              Partners know that going in, which is why only serious ones unlock.
            </Faq>
            <Faq q="Can I list for more than one path at once — financing AND a sale?">
              Yes. A single listing can indicate interest in multiple capital structures. Partners self-select based on what they offer.
              You can also adjust the listing's path mix at any time.
            </Faq>
            <Faq q="What if a partner takes the file and shares it with someone else?">
              Every access event is logged with timestamp, IP, and partner identity. If a partner violates the access
              terms, they lose their account and any other listings they have access to. We have not had to do this often.
            </Faq>
            <Faq q="What does the OfficerApps discount actually look like in practice?">
              If you're an OfficerApps customer and your listing has an acquirer unlock fee of $1,500, an acquirer pays
              $750 to unlock yours. That's a meaningful difference — most acquirers will unlock three OfficerApps listings
              for the price of one non-customer listing.
            </Faq>
          </div>
        </div>
      </section>

      <PartnerEntry />
    </>
  );
}

function PriceRow({ what, price, priceColor = 'var(--oc-ink)', sub }) {
  return (
    <div style={{ padding: '20px 0' }}>
      <div className="oc-row-sb" style={{ alignItems: 'baseline' }}>
        <span style={{ fontSize: 15, color: 'var(--oc-ink)' }}>{what}</span>
        <span style={{ fontFamily: 'var(--oc-font-display)', fontSize: 22, color: priceColor, letterSpacing: '-0.01em' }}>
          {price}
        </span>
      </div>
      {sub && <div style={{ fontSize: 13, color: 'var(--oc-gray)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function PartnerPlan({ label, price, cadence, unlock, points, featured }) {
  return (
    <div
      className="oc-card"
      style={{
        padding: '32px',
        ...(featured ? { borderColor: 'var(--oc-green)', borderWidth: '1px' } : {}),
      }}
    >
      <div className="oc-eyebrow-gray oc-mb-3">{label}</div>
      <div className="oc-row-baseline" style={{ marginBottom: 4 }}>
        <span style={{ fontFamily: 'var(--oc-font-display)', fontSize: 36, color: 'var(--oc-ink)', letterSpacing: '-0.02em' }}>
          {price}
        </span>
        <span style={{ fontSize: 14, color: 'var(--oc-gray)' }}>{cadence}</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--oc-green-deep)', marginBottom: 20 }}>{unlock}</div>
      <hr className="oc-rule oc-mb-3" />
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {points.map((p, i) => (
          <li key={i} className="oc-row" style={{ gap: 10, fontSize: 13, color: 'var(--oc-body)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--oc-green)" strokeWidth="2" style={{ flexShrink: 0 }}>
              <path d="M5 12 l4 4 l10-10" />
            </svg>
            {p}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Faq({ q, children }) {
  return (
    <div>
      <h3 className="oc-h4" style={{ fontSize: 19, marginBottom: 8 }}>{q}</h3>
      <p className="oc-body" style={{ fontSize: 15 }}>{children}</p>
    </div>
  );
}
